import React, { useEffect, useState } from "react"
import styles from "./dashboard.module.css"
import Card from "./Card"
import BreakForm from '../../components/Form/BreakForm/index'
import Select from "../../components/Form/Select"
import api from "../../services/api"
import { toast } from "react-toastify"
import Button from "../../components/Form/Button"
import DatePicker from "../../components/Form/DatePicker"
import Loading from '../layouts/Loading/index'
import Chart from "./Charts/Chart"
import moment from 'moment'
import imageFilterDash from "../../images/filter-dash.png"
import BackgroundSelectFilter from "../../components/BackgroundSelectFilter"
import Table from "../../components/Table"
import FormGroup from './../../components/Form/FormGroup'
import { useData } from "../../contexts/dataContext"

const initialFilters = {
    dateStart: moment().subtract(7, 'd').format("YYYY-MM-DD"),
    dateEnd: moment().subtract(1, 'd').format("YYYY-MM-DD"),
    level: "account",
    breakdowns: [],
    fields: [
        "impressions",
        "clicks",
        "ctr",
        "cpc",
        "cpm",
        "cpp",
        "actions",
        "spend",
        "conversions",
        "cost_per_conversion",
        "reach",
        "frequency",
    ]
}
const levels = [
    { id: "ad", value: "Anúncio" },
    { id: "campaign", value: "Campanha" },
    { id: "adset", value: "Conjunto de Anúncios" },
    { id: "account", value: "Conta" },
]
const breakdowns = [
    { id: "age", value: "Idade" },
    { id: "gender", value: "Gênero" },
    { id: "region", value: "Região" },
    { id: "country", value: "País" },
    { id: "device_platform", value: "Plataforma" },
]
const fields = [
    { id: "impressions", value: "Impressões" },
    { id: "clicks", value: "Cliques" },
    { id: "ctr", value: "Taxa de Cliques" },
    { id: "cpc", value: "Custo por Clique" },
    { id: "cpm", value: "Custo por Mil Impressões" },
    { id: "cpp", value: "Custo por Ação" },
    { id: "actions", value: "Ações" },
    { id: "spend", value: "Gasto Total" },
    { id: "conversions", value: "Conversões" },
    { id: "cost_per_conversion", value: "Custo por Conversão" },
    { id: "reach", value: "Alcance" },
    { id: "frequency", value: "Frequência" },
]

const DashboardMeta = () => {
    const [data, setData] = useState({})
    const [filters, setFilters] = useState(initialFilters)
    const [filteredBreakdowns, setFilteredBreakdowns] = useState([])
    const [loading, setLoading] = useState(false)
    const [backgroundFilter, setBackgroundFilter] = useState(true)

    const { adAccounts } = useData()

    let isLoadingDashboard = false

    useEffect(() => {
        setInitialValuesByFilter("adAccounts", adAccounts?.map(adAccount => { adAccount.selected = true; return adAccount }))
    }, [adAccounts])

    useEffect(() => {
        const filteredBreakdowns = filterOptions(filters.breakdowns)
        setFilteredBreakdowns(filteredBreakdowns)
    }, [filters.breakdowns])

    const getDataByFilter = async () => {
        try {
            if (!isLoadingDashboard) {
                isLoadingDashboard = true
                setLoading(true)
                const queryString = new URLSearchParams(filters)?.toString()
                const response = await api.get(`/leads/dashboard-meta?${queryString}`)
                const responseData = response.data?.data
                setData(responseData)
                setBackgroundFilter(false)
            }
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setLoading(false)
            isLoadingDashboard = false
        }
    }

    const setInitialValuesByFilter = (name, array) => {
        if (array?.length > 0) {
            const newValue = array?.filter(el => el.selected)?.map(el => { return el.id })
            setFilters(previousValue => {
                return {
                    ...previousValue,
                    [name]: newValue
                }
            })
        }
    }

    const handleChangeFilters = (name, value) => {
        setFilters(previousValue => {
            return { ...previousValue, [name]: value }
        })
    }

    const handleChangePeriod = (dateStart, dateEnd) => {
        handleChangeFilters("dateStart", dateStart)
        handleChangeFilters("dateEnd", dateEnd)
    }

    const filterOptions = (selectedIds) => {
        let validOptions = [...breakdowns]

        if (selectedIds.includes("age") || selectedIds.includes("gender")) {
            validOptions = validOptions.filter(b => b.id === "age" || b.id === "gender")
        } else if (selectedIds.includes("region") || selectedIds.includes("country")) {
            validOptions = validOptions.filter(b => b.id === "region" || b.id === "country")
        } else if (selectedIds.length > 0) {
            validOptions = validOptions.filter(b => selectedIds.includes(b.id))
        }

        return validOptions
    }

    return (
        <div className={styles.container}>
            <div className={styles.filtersInline}>
                <FormGroup labelText="Contas de Anúncios">
                    <Select name="Contas de Anúncios" placeholder="Pesquise uma conta" options={adAccounts || []} selected={filters.adAccounts || []} onChange={(value) => handleChangeFilters("adAccounts", value)} multiple={true} />
                </FormGroup>
                <FormGroup labelText="Nível">
                    <Select name="Nível" placeholder="Pesquise um tipo" options={levels || []} selected={filters.level || []} onChange={(value) => handleChangeFilters("level", value)} multiple={false} allowDeselectAll={false} />
                </FormGroup>
                <FormGroup labelText="Segmentação">
                    <Select name="Segmentação" placeholder="Pesquise um tipo" options={filteredBreakdowns || []} selected={filters.breakdowns || []} onChange={(value) => handleChangeFilters("breakdowns", value)} multiple={true} showOptionAll={false} />
                </FormGroup>
                <FormGroup labelText="Campos">
                    <Select name="Campos" placeholder="Pesquise um campo" options={fields || []} selected={filters.fields || []} onChange={(value) => handleChangeFilters("fields", value)} multiple={true} />
                </FormGroup>
                <FormGroup labelText="Período">
                    <DatePicker name="Período" dateStart={filters.dateStart || ""} dateEnd={filters.dateEnd || ""} onChange={(dateStart, dateEnd) => handleChangePeriod(dateStart, dateEnd)} multiple={true} />
                </FormGroup>
                <Button variant='primary' type="submit" onClick={getDataByFilter}>Aplicar Filtros</Button>
            </div>
            <div className={styles.body}>
                {!backgroundFilter &&
                    <>
                        <div className={styles.cards}>
                            {data?.cards?.length > 0 && data.cards.map(item => (
                                <Card
                                    key={item.id}
                                    name={item.name}
                                    value={item.value}
                                    description={item.description}
                                    tooltip={item.tooltip || item.description}
                                />
                            ))}
                        </div>
                        <div className={styles.charts}>
                            {data?.charts?.length > 0 && data.charts.map(item => (
                                <Chart
                                    key={item.id}
                                    data={item}
                                />
                            ))}
                        </div>
                        <div className={styles.tables}>
                            {data?.tables?.length > 0 && data.tables.map((table, index) => (
                                <div className={`${styles.groupTable} ${styles.fullWidth}`} key={index}>
                                    <BreakForm title={table.title} showAction={false} />
                                    <Table
                                        headers={table.headers}
                                        values={table.values}
                                    />
                                </div>
                            ))}
                        </div>
                    </>
                }
                {backgroundFilter &&
                    <BackgroundSelectFilter
                        text="Selecione os filtros desejados e aplique para visualizar os dados!"
                        image={imageFilterDash}
                    />
                }
            </div>
            {loading && <Loading fullPage={true} />}
        </div>
    )
}

export default DashboardMeta
